/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const TRANSFER_PROGRAM_AUTHORITY_DISCRIMINATOR = 10;

export function getTransferProgramAuthorityDiscriminatorBytes() {
  return getU8Encoder().encode(TRANSFER_PROGRAM_AUTHORITY_DISCRIMINATOR);
}

export type TransferProgramAuthorityInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountProgramAuthority extends string | AccountMeta<string> = string,
  TAccountCurrentAuthority extends string | AccountMeta<string> = string,
  TAccountNewAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountProgramAuthority extends string
        ? WritableAccount<TAccountProgramAuthority>
        : TAccountProgramAuthority,
      TAccountCurrentAuthority extends string
        ? ReadonlySignerAccount<TAccountCurrentAuthority> &
            AccountSignerMeta<TAccountCurrentAuthority>
        : TAccountCurrentAuthority,
      TAccountNewAuthority extends string
        ? ReadonlyAccount<TAccountNewAuthority>
        : TAccountNewAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type TransferProgramAuthorityInstructionData = { discriminator: number };

export type TransferProgramAuthorityInstructionDataArgs = {};

export function getTransferProgramAuthorityInstructionDataEncoder(): FixedSizeEncoder<TransferProgramAuthorityInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', getU8Encoder()]]),
    (value) => ({
      ...value,
      discriminator: TRANSFER_PROGRAM_AUTHORITY_DISCRIMINATOR,
    })
  );
}

export function getTransferProgramAuthorityInstructionDataDecoder(): FixedSizeDecoder<TransferProgramAuthorityInstructionData> {
  return getStructDecoder([['discriminator', getU8Decoder()]]);
}

export function getTransferProgramAuthorityInstructionDataCodec(): FixedSizeCodec<
  TransferProgramAuthorityInstructionDataArgs,
  TransferProgramAuthorityInstructionData
> {
  return combineCodec(
    getTransferProgramAuthorityInstructionDataEncoder(),
    getTransferProgramAuthorityInstructionDataDecoder()
  );
}

export type TransferProgramAuthorityInput<
  TAccountProgramAuthority extends string = string,
  TAccountCurrentAuthority extends string = string,
  TAccountNewAuthority extends string = string,
> = {
  /** The program authority PDA */
  programAuthority: Address<TAccountProgramAuthority>;
  /** The current program authority */
  currentAuthority: TransactionSigner<TAccountCurrentAuthority>;
  /** The new authority to nominate */
  newAuthority: Address<TAccountNewAuthority>;
};

export function getTransferProgramAuthorityInstruction<
  TAccountProgramAuthority extends string,
  TAccountCurrentAuthority extends string,
  TAccountNewAuthority extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: TransferProgramAuthorityInput<
    TAccountProgramAuthority,
    TAccountCurrentAuthority,
    TAccountNewAuthority
  >,
  config?: { programAddress?: TProgramAddress }
): TransferProgramAuthorityInstruction<
  TProgramAddress,
  TAccountProgramAuthority,
  TAccountCurrentAuthority,
  TAccountNewAuthority
> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    programAuthority: {
      value: input.programAuthority ?? null,
      isWritable: true,
    },
    currentAuthority: {
      value: input.currentAuthority ?? null,
      isWritable: false,
    },
    newAuthority: { value: input.newAuthority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.programAuthority),
      getAccountMeta(accounts.currentAuthority),
      getAccountMeta(accounts.newAuthority),
    ],
    data: getTransferProgramAuthorityInstructionDataEncoder().encode({}),
    programAddress,
  } as TransferProgramAuthorityInstruction<
    TProgramAddress,
    TAccountProgramAuthority,
    TAccountCurrentAuthority,
    TAccountNewAuthority
  >);
}

export type ParsedTransferProgramAuthorityInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The program authority PDA */
    programAuthority: TAccountMetas[0];
    /** The current program authority */
    currentAuthority: TAccountMetas[1];
    /** The new authority to nominate */
    newAuthority: TAccountMetas[2];
  };
  data: TransferProgramAuthorityInstructionData;
};

export function parseTransferProgramAuthorityInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedTransferProgramAuthorityInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      programAuthority: getNextAccount(),
      currentAuthority: getNextAccount(),
      newAuthority: getNextAccount(),
    },
    data: getTransferProgramAuthorityInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
